<template>
    <div
        ref="renderer"
        class="relative h-full flex justify-center items-center transition-all duration-300 ease-out"
        :style="{ top: `${topOffset}px` }"
        :class="isFullscreen ? 'w-screen' : 'w-full'"
    >
        <div
            ref="placeholder"
            class="product-renderer__image relative w-full h-full flex justify-center items-center overflow-hidden bg-gray-F0F0F0"
            :class="{ 'product-renderer__image--toolbar-space': showToolbar }"
        >
        </div>

        <button
            v-if="showFullscreenButton"
            @click="toggleFullscreen"
            :class="[
                'absolute top-6 right-6 lg:top-10 lg:right-10 flex justify-center items-center transition-all duration-300 ease-out',
                'focus:outline-none focus:ring-1 focus:ring-black focus:border-black ring-offset-2',
            ]"
        >
            <img
                v-if="isFullscreen"
                src="../assets/images/icons/compress.svg"
                width="24"
                height="24"
                alt="Compress"
            >
            <img
                v-else
                src="../assets/images/icons/expand.svg"
                width="24"
                height="24"
                alt="Expand"
            >
        </button>

        <!-- toolbar centered along bottom -->
        <div
            v-if="showToolbar"
            class="absolute bottom-[20px] lg:bottom-[26px] left-1/2 transform -translate-x-1/2"
        >
            <camera-angle-toolbar
                :active-view="activeView"
                @select-view="setActiveView"
            />
        </div>

        <!-- <div
            class="absolute bottom-[20px] lg:bottom-[32px] cz-hidden lg:block"
            :class="canShare ? 'right-[108px]' : 'right-[16px] lg:right-10'"
        >
            <share-button />
        </div> -->

        <div class="absolute bottom-[20px] lg:bottom-[32px] right-[16px] lg:right-10">
            <transition name="cz3__fade">
                <share-button v-if="!isMobile" />
            </transition>
        </div>

        <help-button class="absolute bottom-[20px] lg:bottom-[32px] left-[16px] lg:left-10 cz-hidden lg:block" />

        <!-- <transition name="cz3__fade">
            <div
                v-if="isLoading"
                class="absolute inset-0 bg-white opacity-80 cursor-wait pointer-events-none"
            >
            </div>
        </transition> -->
    </div>
</template>

<script>
    import allStores from '../stores/all';

    import CameraAngleToolbar from './CameraAngleToolbar.vue';
    import HelpButton from './buttons/HelpButton.vue';
    import ShareButton from './buttons/ShareButton.vue';

    export default {
        mixins: [allStores],

        components: {
            CameraAngleToolbar,
            HelpButton,
            ShareButton,
        },

        data() {
            return {
            };
        },

        props: {
            showToolbar: {
                type: Boolean,
                default: true,
            },

            showZoom: {
                type: Boolean,
                default: true,
            },

            showFullscreenButton: {
                type: Boolean,
                default: false,
            },

            imageZoomPercent: {
                type: Number,
                default: 100,
            },

            topOffset: {
                type: Number,
                default: 0,
            },
        },

        computed: {
            isMobile() {
                return this.appStore.isMobile;
            },

            currentStep() {
                return this.appStore.currentStep;
            },

            isLoading() {
                return this.appStore.isLoading;
            },

            lastRenderedImage() {
                return this.customizerStore.lastRenderedImage;
            },

            isFullscreen() {
                return this.appStore.isFullscreen;
            },

            canShare() {
                return this.customizerStore.selectedProducts.length > 0;
            },

            activeView() {
                return this.customizerStore.activeView;
            },
        },

        watch: {
            currentStep() {
                this.$nextTick(() => {
                    this.resize();
                });
            },

            lastRenderedImage() {
                this.showImage();
            },

            imageZoomPercent() {
                this.applyImageZoom();
            },
        },

        mounted() {
            this.resize();
            this.showImage();
        },

        methods: {
            toggleFullscreen() {
                if (this.isMobile) {
                    this.appStore.openNestedPanel('preview-panel', {
                        showToolbar: this.showToolbar,
                    });
                } else {
                    let newSize = false;

                    if (!this.isFullscreen) {
                        newSize = true;
                    }

                    this.appStore.setIsFullscreen(newSize);
                }
            },

            resize() {
                const parent = this.$el.closest('.cz3');

                if (parent) {
                    const excludes = Array.from(parent.querySelectorAll('.cz3-renderer-exclude'));

                    let excludeHeight = 0;

                    // Margins.
                    const isLarge = window.innerWidth > 1024;

                    if (isLarge) {
                        excludeHeight += 24 + 24;
                    } else {
                        excludeHeight += 16;
                    }

                    excludes.forEach((el) => {
                        excludeHeight += el.offsetHeight;
                    });

                    this.$refs.renderer.style.setProperty('--exclude-height', `${excludeHeight}px`);
                }
            },

            showImage() {
                const container = this.$refs.placeholder;

                if (!this.lastRenderedImage) {
                    while (container.firstChild) {
                        container.removeChild(container.lastChild);
                    }

                    return;
                }

                // The images have transparent backgrounds, so crossfade: fade out
                // every layer currently shown while the new one fades in.
                Array.from(container.children).forEach((old) => {
                    this.fadeOutLayer(old);
                });

                // Stack the new render and fade it in.
                const layer = document.createElement('div');

                layer.className = 'absolute top-0 left-0 right-0 flex justify-center items-start overflow-hidden';
                layer.style.opacity = '0';
                layer.style.transition = 'opacity 0.4s ease-in-out';

                const image = this.lastRenderedImage.cloneNode(true);
                image.classList.add('w-11/12', 'h-full', 'max-w-[1200px]');
                image.dataset.cz3ProductRenderImage = 'true';
                this.applyImageZoomToImage(image);

                layer.appendChild(image);
                container.appendChild(layer);

                // Force reflow so the opacity 0 -> 1 change animates.
                layer.getBoundingClientRect();
                layer.style.opacity = '1';
            },

            applyImageZoom() {
                if (!this.$refs.placeholder) {
                    return;
                }

                const images = this.$refs.placeholder.querySelectorAll('[data-cz3-product-render-image="true"]');

                images.forEach((image) => {
                    this.applyImageZoomToImage(image);
                });
            },

            applyImageZoomToImage(image) {
                const zoomPercent = Number.isFinite(this.imageZoomPercent)
                    ? this.imageZoomPercent
                    : 100;
                const zoomScale = Math.max(0, zoomPercent) / 100;

                image.style.transform = `scale(${zoomScale})`;
                image.style.transformOrigin = 'center center';
            },

            // Fade a layer out, then remove it from the DOM.
            fadeOutLayer(layer) {
                if (layer.dataset.cz3FadingOut) {
                    return;
                }

                layer.dataset.cz3FadingOut = 'true';

                const remove = () => {
                    if (layer.parentNode) {
                        layer.parentNode.removeChild(layer);
                    }
                };

                // Remove once the fade completes, with a fallback in case
                // transitionend never fires (e.g. the tab is hidden).
                // remove() is idempotent, so both paths are safe.
                layer.addEventListener('transitionend', (e) => {
                    if (e.propertyName === 'opacity') {
                        remove();
                    }
                }, { once: true });

                setTimeout(remove, 500);

                layer.getBoundingClientRect();
                layer.style.opacity = '0';
            },

            setActiveView(view) {
                this.customizerStore.activeView = view;

                this.customizerStore.updatePreviewImage();
            },
        },
    };
</script>

<style>
    :root {
        --camera-angle-toolbar-buffer: 40px;
    }

    /* Layers are created in JS; shorten from the bottom only (toolbar buffer). */
    #cz3 .product-renderer__image > div {
        bottom: 0;
    }

    @media (max-width: 1024px) {
        #cz3 .product-renderer__image--toolbar-space > div {
            bottom: var(--camera-angle-toolbar-buffer);
        }
    }
</style>
